/**
 * 예산 현액(가용·집행가능) PoC
 * - sumDelta: 차수별 증감 현액 적재 · 성립전 확정→즉시 · 추경 본편 확정 시 성립전 현액 흡수
 * - 간주: 구분코드 끝까지 별도 유지
 * - lastRound: 확정 차수 절대액으로 당해 편성확정 현액 치환
 * - 이월은 현액 이벤트만 (차수 아님)
 *
 * SSOT: D:\295\erp\BD\design\09_현액_가용예산.md
 */
import { createFundUid } from 'budget/budgetFundStore';
import { acctClassLabel } from 'budget/budgetAcctStore';
import {
  BUDGET_CLASS_LABEL,
  getBudgetRoundByKindCode,
  computeFinalAdjNo,
  normalizeBudgetClass,
} from 'budget/budgetRoundStore';
import {
  buildBudgetDimKey,
  isMainFinalRound,
  isSeonglipjeonRound,
  isGanjuRound,
  isFinalBudgetRound,
  listConfirmedFinalRounds,
  listConfirmedSeonglipjeonForParent,
  loadRoundFormationRows,
  getSettleParentKindCode,
} from 'budget/budgetBaselineStore';
import { getFinalBudgetMode, FINAL_BUDGET_MODE } from 'budget/budgetTenantSettingsStore';
import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList, cacheGet, cacheSet } from 'services/commonQuery';

export const BALANCE_STORAGE_PREFIX = 'budgetBalance';

const BALANCE_SAVE_QUERY = [
  'BdBudgetBalance.insertBalance',
  'BdBudgetBalance.updateBalance',
  'BdBudgetBalance.deleteBalance',
];

const balanceCache = new Map();

export const YEAR_NATURE = {
  CURRENT: '당해',
  CARRY: '이월',
};

export const YEAR_NATURE_OPTIONS = [
  { value: '', label: '전체' },
  { value: YEAR_NATURE.CURRENT, label: '당해' },
  { value: YEAR_NATURE.CARRY, label: '이월' },
];

export const ORIGIN_TYPE = {
  CONFIRM: '편성확정',
  CARRY_IN: '이월유입',
  TRANSFER_IN: '전용입',
  TRANSFER_OUT: '전용출',
  MANUAL: '수기보정',
};

export const ORIGIN_TYPE_OPTIONS = [
  { value: '', label: '전체' },
  { value: ORIGIN_TYPE.CONFIRM, label: '편성확정' },
  { value: ORIGIN_TYPE.CARRY_IN, label: '이월유입' },
  { value: ORIGIN_TYPE.TRANSFER_IN, label: '전용입' },
  { value: ORIGIN_TYPE.TRANSFER_OUT, label: '전용출' },
  { value: ORIGIN_TYPE.MANUAL, label: '수기보정' },
];

export const INOUT_FILTER_OPTIONS = [
  { value: '', label: '전체' },
  { value: '지출', label: '지출' },
  { value: '수입', label: '수입' },
];

export function createBalanceUid() {
  return createFundUid('BL');
}

export function balanceStorageKey({ tenantCode, fiscalYear }) {
  return [BALANCE_STORAGE_PREFIX, tenantCode || '', fiscalYear || ''].join('|');
}

function readAll(dims) {
  try {
    const raw = localStorage.getItem(balanceStorageKey(dims));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.rows) ? parsed.rows : [];
  } catch {
    return [];
  }
}

function writeAll(dims, rows) {
  localStorage.setItem(
    balanceStorageKey(dims),
    JSON.stringify({
      dims: { tenantCode: dims.tenantCode, fiscalYear: dims.fiscalYear },
      rows: rows || [],
      savedAt: new Date().toISOString(),
    })
  );
}

/** 현액 · 가용 재계산 */
export function enrichBalanceRow(row) {
  const confirmedAmt = Number(row.confirmedAmt) || 0;
  const carryInAmt = Number(row.carryInAmt) || 0;
  const transferInAmt = Number(row.transferInAmt) || 0;
  const transferOutAmt = Number(row.transferOutAmt) || 0;
  const obligAmt = Number(row.obligAmt) || 0;
  const execAmt = Number(row.execAmt) || 0;
  const balanceAmt = confirmedAmt + carryInAmt + transferInAmt - transferOutAmt;
  const availableAmt = balanceAmt - obligAmt - execAmt;
  const balanceUid = row.balanceUid || createBalanceUid();
  return {
    ...row,
    balanceUid,
    id: row.id || balanceUid,
    confirmedAmt,
    carryInAmt,
    transferInAmt,
    transferOutAmt,
    obligAmt,
    execAmt,
    balanceAmt,
    availableAmt,
  };
}

/** 동기 조회용 — load 가 채운 캐시(API) 또는 localStorage(로컬) */
export function getCachedBalances(dims) {
  const key = balanceStorageKey(dims);
  const cached = cacheGet(balanceCache, key);
  if (cached) return cached;
  if (isBdApiEnabled()) return [];
  const rows = readAll(dims).map((r) => enrichBalanceRow(r));
  cacheSet(balanceCache, key, rows);
  return rows;
}

export async function loadBalances(dims) {
  const key = balanceStorageKey(dims);
  if (isBdApiEnabled()) {
    const rows = await commonSelect('BdBudgetBalance.selectBalanceList', {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
      // superseded 행도 현액 재구성 판단에 필요하므로 전량 조회
      excludeSuperseded: 'N',
    });
    const mapped = rows.map((r) =>
      enrichBalanceRow({
        ...r,
        originType: r.originType === 'CONFIRM' ? ORIGIN_TYPE.CONFIRM : r.originType,
        yearNature:
          r.yearNature === 'CURRENT'
            ? YEAR_NATURE.CURRENT
            : r.yearNature === 'CARRY'
              ? YEAR_NATURE.CARRY
              : r.yearNature,
      })
    );
    cacheSet(balanceCache, key, mapped);
    return mapped;
  }
  const rows = readAll(dims).map((r) => enrichBalanceRow(r));
  cacheSet(balanceCache, key, rows);
  return rows;
}

export async function saveBalances(dims, rows) {
  const persistable = (rows || []).map((r) => {
    const e = enrichBalanceRow(r);
    return {
      balanceUid: e.balanceUid,
      tenantCode: dims.tenantCode,
      fiscalYear: dims.fiscalYear,
      budgetKindCode: e.budgetKindCode || '',
      budgetClass: e.budgetClass || '',
      budgetClassNm: e.budgetClassNm || '',
      roundName: e.roundName || '',
      inoutDiv: e.inoutDiv || '지출',
      yearNature: e.yearNature || YEAR_NATURE.CURRENT,
      originType: e.originType || ORIGIN_TYPE.CONFIRM,
      deptCode: e.deptCode || '',
      deptName: e.deptName || '',
      projectCode: e.projectCode || '',
      projectName: e.projectName || '',
      subjectUid: e.subjectUid || '',
      subjectCode: e.subjectCode || '',
      subjectDispCode: e.subjectDispCode || e.subjectCode || '',
      subjectName: e.subjectName || '',
      fundUid: e.fundUid || '',
      fundCode: e.fundCode || '',
      fundName: e.fundName || '',
      acctClassCode: e.acctClassCode || '',
      acctClassNm: e.acctClassNm || '',
      sourceAdjNo: e.sourceAdjNo != null ? Number(e.sourceAdjNo) : null,
      sourceRequestUid: e.sourceRequestUid || '',
      sourceKindCode: e.sourceKindCode || '',
      supersededByKindCode: e.supersededByKindCode || '',
      confirmedAmt: e.confirmedAmt,
      carryInAmt: e.carryInAmt,
      transferInAmt: e.transferInAmt,
      transferOutAmt: e.transferOutAmt,
      obligAmt: e.obligAmt,
      execAmt: e.execAmt,
      remark: e.remark || '',
      confirmedAt: e.confirmedAt || null,
      confirmedBy: e.confirmedBy || '',
    };
  });

  const key = balanceStorageKey(dims);
  if (isBdApiEnabled()) {
    // 현액은 전량 재적재 성격 → DB 현재 상태와 diff 하여 A/U/D 산출
    const existing = await commonSelect('BdBudgetBalance.selectBalanceList', {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
      excludeSuperseded: 'N',
    });
    const nextUids = new Set(persistable.map((r) => r.balanceUid));
    const dbUids = new Set(existing.map((r) => r.balanceUid));
    const dirty = [
      ...existing
        .filter((r) => !nextUids.has(r.balanceUid))
        .map((r) => ({ balanceUid: r.balanceUid, mod: 'D' })),
      ...persistable.map((r) => ({ ...r, mod: dbUids.has(r.balanceUid) ? 'U' : 'A' })),
    ];
    await commonSaveList(BALANCE_SAVE_QUERY, dirty, {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
    });
    return loadBalances(dims);
  }

  writeAll(dims, persistable);
  const saved = persistable.map((r) => enrichBalanceRow(r));
  cacheSet(balanceCache, key, saved);
  return saved;
}

export function filterBalances(rows = [], filter = {}) {
  let list = (rows || []).map((r) => enrichBalanceRow(r));
  const kind = String(filter.budgetKindCode || '').trim();
  if (kind) list = list.filter((r) => String(r.budgetKindCode) === kind);
  const inout = String(filter.inoutDiv || '').trim();
  if (inout) list = list.filter((r) => r.inoutDiv === inout);
  const yn = String(filter.yearNature || '').trim();
  if (yn) list = list.filter((r) => r.yearNature === yn);
  const origin = String(filter.originType || '').trim();
  if (origin) list = list.filter((r) => r.originType === origin);
  const acct = String(filter.acctClass || '').trim();
  if (acct) list = list.filter((r) => String(r.acctClassCode || '') === acct);
  const kw = String(filter.deptKeyword || '').trim();
  if (kw) {
    list = list.filter(
      (r) =>
        String(r.deptCode || '').includes(kw) || String(r.deptName || '').includes(kw)
    );
  }
  const pkw = String(filter.projectKeyword || '').trim();
  if (pkw) {
    list = list.filter(
      (r) =>
        String(r.projectCode || '').includes(pkw) ||
        String(r.projectName || '').includes(pkw)
    );
  }
  return list;
}

function lineKey(r) {
  return [
    r.inoutDiv || '',
    r.deptCode || '',
    r.projectCode || '',
    r.subjectUid || r.subjectCode || '',
    r.fundUid || r.fundCode || '',
    r.acctClassCode || '',
  ].join('|');
}

function mapFormationToBalance(row, meta, amount) {
  const amt = Number(amount);
  const confirmedAmt = Number.isFinite(amt) ? amt : Number(row.budgetAmt) || 0;
  return enrichBalanceRow({
    balanceUid: createBalanceUid(),
    budgetKindCode: meta.budgetKindCode,
    budgetClass: meta.budgetClass,
    budgetClassNm: meta.budgetClassNm,
    roundName: meta.roundName,
    inoutDiv: meta.inoutDiv,
    yearNature: YEAR_NATURE.CURRENT,
    originType: meta.originType || ORIGIN_TYPE.CONFIRM,
    deptCode: row.deptCode || '',
    deptName: row.deptName || '',
    projectCode: row.projectCode || '',
    projectName: row.projectName || '',
    subjectUid: row.subjectUid || '',
    subjectCode: row.subjectCode || '',
    subjectDispCode: row.subjectDispCode || row.subjectCode || '',
    subjectName: row.subjectName || '',
    fundUid: row.fundUid || '',
    fundCode: row.fundCode || '',
    fundName: row.fundName || '',
    acctClassCode: row.acctClassCode || '',
    acctClassNm:
      row.acctClassNm ||
      (row.acctClassCode ? acctClassLabel(row.acctClassCode, meta.tenantCode) : ''),
    sourceAdjNo: meta.sourceAdjNo,
    sourceRequestUid: row.requestUid || '',
    sourceKindCode: meta.sourceKindCode || '',
    supersededByKindCode: meta.supersededByKindCode || '',
    confirmedAmt,
    carryInAmt: 0,
    transferInAmt: 0,
    transferOutAmt: 0,
    obligAmt: Number(row._carryObligAmt) || 0,
    execAmt: Number(row._carryExecAmt) || 0,
    remark: meta.remark || '',
    confirmedAt: meta.confirmedAt,
    confirmedBy: meta.confirmedBy,
    finalBudgetMode: meta.finalBudgetMode || '',
  });
}

/** 당해 편성확정 현액 행 */
function isConfirmCurrentRow(row) {
  return row.originType === ORIGIN_TYPE.CONFIRM && row.yearNature === YEAR_NATURE.CURRENT;
}

/** 차원 키(차수 제외) — 집행액 이관용 */
function execDimKey(row) {
  return lineKey(row);
}

/** 기존 현액의 원인행위·집행을 새 행에 이관 (결합코드+차원 우선, 없으면 차원만 1회) */
function applyExecCarry(newRows, oldConfirmRows) {
  const bagKind = new Map();
  const bagDim = new Map();
  const add = (map, key, oblig, exec) => {
    const prev = map.get(key) || { oblig: 0, exec: 0 };
    prev.oblig += oblig;
    prev.exec += exec;
    map.set(key, prev);
  };
  (oldConfirmRows || []).forEach((r) => {
    const d = execDimKey(r);
    const oblig = Number(r.obligAmt) || 0;
    const exec = Number(r.execAmt) || 0;
    if (!oblig && !exec) return;
    add(bagKind, `${r.budgetKindCode || ''}||${d}`, oblig, exec);
    add(bagDim, d, oblig, exec);
  });
  return (newRows || []).map((r) => {
    const d = execDimKey(r);
    const kd = `${r.budgetKindCode || ''}||${d}`;
    let got = bagKind.get(kd);
    if (got) {
      bagKind.delete(kd);
      const dimLeft = bagDim.get(d);
      if (dimLeft) {
        dimLeft.oblig = Math.max(0, dimLeft.oblig - got.oblig);
        dimLeft.exec = Math.max(0, dimLeft.exec - got.exec);
        if (!dimLeft.oblig && !dimLeft.exec) bagDim.delete(d);
        else bagDim.set(d, dimLeft);
      }
    } else {
      got = bagDim.get(d);
      if (got) bagDim.delete(d);
    }
    if (!got) return r;
    return enrichBalanceRow({
      ...r,
      obligAmt: (Number(r.obligAmt) || 0) + got.oblig,
      execAmt: (Number(r.execAmt) || 0) + got.exec,
    });
  });
}

function roundMeta(tenantCode, round, confirmedAt, confirmedBy, remark) {
  const kind = String(round.budgetKindCode);
  const adjNo =
    round.finalAdjNo != null && round.finalAdjNo !== ''
      ? Number(round.finalAdjNo)
      : computeFinalAdjNo(round.adjStages);
  const budgetClass = normalizeBudgetClass(round.budgetClass);
  return {
    tenantCode,
    budgetKindCode: kind,
    budgetClass,
    budgetClassNm: BUDGET_CLASS_LABEL[budgetClass] || '',
    roundName: round.roundName || '',
    confirmedAt: confirmedAt || round.budgetConfirmAt || new Date().toISOString(),
    confirmedBy: confirmedBy || round.budgetConfirmBy || '',
    sourceAdjNo: adjNo,
    originType: ORIGIN_TYPE.CONFIRM,
    finalBudgetMode: getFinalBudgetMode(tenantCode),
    remark: remark || '',
  };
}

/** 단일 차수 증감(또는 절대) → 현액 행 */
function buildRoundOnlyBalances(tenantCode, fiscalYear, round, confirmedAt, confirmedBy, remark) {
  const metaBase = roundMeta(tenantCode, round, confirmedAt, confirmedBy, remark);
  const built = [];
  let expenseCount = 0;
  let incomeCount = 0;
  ['지출', '수입'].forEach((inoutDiv) => {
    const rows = loadRoundFormationRows(
      tenantCode,
      fiscalYear,
      round.budgetKindCode,
      inoutDiv
    );
    rows.forEach((r) => {
      const amt = Number(r.budgetAmt) || 0;
      if (!amt && !r.deptCode && !r.subjectCode) return;
      built.push(mapFormationToBalance(r, { ...metaBase, inoutDiv }, amt));
      if (inoutDiv === '지출') expenseCount += 1;
      else incomeCount += 1;
    });
  });
  return { rows: built, expenseCount, incomeCount, sourceAdjNo: metaBase.sourceAdjNo };
}

/**
 * sumDelta: 차수별 증감 적재 + 성립전→확정된 추경 본편으로 흡수
 */
function buildSumDeltaBalances(tenantCode, fiscalYear) {
  const rounds = listConfirmedFinalRounds(tenantCode, fiscalYear);
  const confirmedParents = new Set(
    rounds.filter((r) => isMainFinalRound(r)).map((r) => String(r.budgetKindCode))
  );

  const built = [];
  let expenseCount = 0;
  let incomeCount = 0;

  rounds.forEach((round) => {
    const kind = String(round.budgetKindCode);

    // 성립전: 부모 추경 본편이 이미 확정이면 현액 버킷에서 제외(흡수됨)
    if (isSeonglipjeonRound(round)) {
      const parent = getSettleParentKindCode(round);
      if (parent && confirmedParents.has(parent)) return;
      const part = buildRoundOnlyBalances(
        tenantCode,
        fiscalYear,
        round,
        null,
        null,
        '성립전·선집행'
      );
      built.push(...part.rows);
      expenseCount += part.expenseCount;
      incomeCount += part.incomeCount;
      return;
    }

    // 간주: 끝까지 별도 구분
    if (isGanjuRound(round)) {
      const part = buildRoundOnlyBalances(
        tenantCode,
        fiscalYear,
        round,
        null,
        null,
        '간주예산·구분유지'
      );
      built.push(...part.rows);
      expenseCount += part.expenseCount;
      incomeCount += part.incomeCount;
      return;
    }

    // 본·추경 본편: 본편 증감 + (확정된) 계상 성립전 증감
    if (!isMainFinalRound(round)) return;

    const seonglips = listConfirmedSeonglipjeonForParent(tenantCode, fiscalYear, kind);
    const metaBase = roundMeta(
      tenantCode,
      round,
      null,
      null,
      seonglips.length
        ? `합산증감+성립전흡수(${seonglips.map((s) => s.budgetKindCode).join(',')})`
        : '차수증감'
    );

    ['지출', '수입'].forEach((inoutDiv) => {
      const filterDims = { inoutDiv };
      const amtMap = new Map();
      const tplMap = new Map();
      const sourceKinds = new Map();

      const addRows = (srcRound, asSource) => {
        const rows = loadRoundFormationRows(
          tenantCode,
          fiscalYear,
          srcRound.budgetKindCode,
          inoutDiv
        );
        rows.forEach((r) => {
          const key = buildBudgetDimKey(r, filterDims);
          const amt = Number(r.budgetAmt) || 0;
          amtMap.set(key, (amtMap.get(key) || 0) + amt);
          if (!tplMap.has(key)) tplMap.set(key, r);
          if (asSource) {
            const prev = sourceKinds.get(key) || [];
            prev.push(String(srcRound.budgetKindCode));
            sourceKinds.set(key, prev);
          }
        });
      };

      addRows(round, false);
      seonglips.forEach((s) => addRows(s, true));

      amtMap.forEach((amt, key) => {
        const tpl = tplMap.get(key) || { budgetAmt: amt };
        const src = (sourceKinds.get(key) || []).join(',');
        built.push(
          mapFormationToBalance(
            tpl,
            {
              ...metaBase,
              inoutDiv,
              sourceKindCode: src,
              remark: src
                ? `${metaBase.remark || ''} · 흡수원천:${src}`.trim()
                : metaBase.remark,
            },
            amt
          )
        );
        if (inoutDiv === '지출') expenseCount += 1;
        else incomeCount += 1;
      });
    });
  });

  return {
    rows: built,
    expenseCount,
    incomeCount,
    mode: FINAL_BUDGET_MODE.SUM_DELTA,
  };
}

/**
 * lastRound: 마지막 확정 차수 절대액 = 당해 편성확정 현액 SSOT
 */
function buildLastRoundBalances(tenantCode, fiscalYear, upToKindCode) {
  const rounds = listConfirmedFinalRounds(tenantCode, fiscalYear, upToKindCode);
  if (!rounds.length) {
    return { rows: [], expenseCount: 0, incomeCount: 0, mode: FINAL_BUDGET_MODE.LAST_ROUND };
  }
  const latest = rounds[rounds.length - 1];
  const part = buildRoundOnlyBalances(
    tenantCode,
    fiscalYear,
    latest,
    null,
    null,
    `최종차수(${latest.budgetKindCode})`
  );
  return {
    rows: part.rows,
    expenseCount: part.expenseCount,
    incomeCount: part.incomeCount,
    mode: FINAL_BUDGET_MODE.LAST_ROUND,
    sourceAdjNo: part.sourceAdjNo,
  };
}

/**
 * 예산확정 차수 → 현액 재적재
 */
export async function rebuildBalancesOnBudgetConfirm({
  tenantCode,
  fiscalYear,
  budgetKindCode,
  confirmedAt,
  confirmedBy,
} = {}) {
  const kind = String(budgetKindCode || '').trim();
  if (!tenantCode || !fiscalYear || !kind) {
    return { ok: false, reason: '기관·연도·결합코드가 필요합니다.', count: 0 };
  }
  const round = getBudgetRoundByKindCode(tenantCode, fiscalYear, kind);
  if (!round) {
    return { ok: false, reason: `예산차수(${kind})를 찾을 수 없습니다.`, count: 0 };
  }
  if (round.budgetConfirmYn !== 'Y') {
    return { ok: false, reason: '예산확정된 차수만 현액에 반영할 수 있습니다.', count: 0 };
  }
  if (!isFinalBudgetRound(round)) {
    return {
      ok: false,
      reason: '본·추경·성립전·간주 차수만 현액 반영 대상입니다.',
      count: 0,
    };
  }

  const existing = await loadBalances({ tenantCode, fiscalYear });
  const oldConfirm = existing.filter(isConfirmCurrentRow);
  const kept = existing.filter((r) => !isConfirmCurrentRow(r));
  const mode = getFinalBudgetMode(tenantCode);

  let built;
  if (mode === FINAL_BUDGET_MODE.LAST_ROUND) {
    built = buildLastRoundBalances(tenantCode, fiscalYear, kind);
  } else {
    built = buildSumDeltaBalances(tenantCode, fiscalYear);
  }

  if (!built.rows.length) {
    // 성립전만 있고 금액 0 등 — 빈 재적재도 허용하되 경고
    await saveBalances({ tenantCode, fiscalYear }, kept);
    return {
      ok: false,
      reason:
        '반영할 편성(조정/요구) 데이터가 없습니다. 수입·지출 편성조정 또는 편성요구를 확인하세요.',
      count: 0,
    };
  }

  const withExec = applyExecCarry(built.rows, oldConfirm);
  // confirmedAt/By 를 이번 트리거 차수에 반영
  const stamped = withExec.map((r) =>
    enrichBalanceRow({
      ...r,
      confirmedAt: confirmedAt || r.confirmedAt,
      confirmedBy: confirmedBy || r.confirmedBy,
    })
  );

  await saveBalances({ tenantCode, fiscalYear }, [...kept, ...stamped]);

  const absorbed = isMainFinalRound(round)
    ? listConfirmedSeonglipjeonForParent(tenantCode, fiscalYear, kind).length
    : 0;

  return {
    ok: true,
    count: stamped.length,
    expenseCount: built.expenseCount,
    incomeCount: built.incomeCount,
    sourceAdjNo: built.sourceAdjNo,
    mode: built.mode,
    absorbedSeonglipCount: absorbed,
  };
}

/**
 * 예산확정 취소 → 당해 편성확정 현액 재구성
 */
export async function clearConfirmBalancesOnUnconfirm({
  tenantCode,
  fiscalYear,
  budgetKindCode,
} = {}) {
  const kind = String(budgetKindCode || '').trim();
  if (!tenantCode || !fiscalYear || !kind) {
    return { ok: false, reason: '기관·연도·결합코드가 필요합니다.', removed: 0 };
  }

  const existing = await loadBalances({ tenantCode, fiscalYear });
  const oldConfirm = existing.filter(isConfirmCurrentRow);
  const kept = existing.filter((r) => !isConfirmCurrentRow(r));
  const mode = getFinalBudgetMode(tenantCode);

  // 취소 대상 차수는 호출 측에서 이미 confirmYn=N 으로 저장된 뒤 호출됨
  const remaining = listConfirmedFinalRounds(tenantCode, fiscalYear);

  let rebuilt = { rows: [], expenseCount: 0, incomeCount: 0 };
  if (remaining.length) {
    if (mode === FINAL_BUDGET_MODE.LAST_ROUND) {
      const latestKind = String(remaining[remaining.length - 1].budgetKindCode);
      rebuilt = buildLastRoundBalances(tenantCode, fiscalYear, latestKind);
    } else {
      rebuilt = buildSumDeltaBalances(tenantCode, fiscalYear);
    }
  }

  const withExec = applyExecCarry(rebuilt.rows, oldConfirm);
  await saveBalances({ tenantCode, fiscalYear }, [...kept, ...withExec]);

  return {
    ok: true,
    removed: oldConfirm.length,
    hadExecution: oldConfirm.some(
      (r) => (Number(r.execAmt) || 0) + (Number(r.obligAmt) || 0) > 0
    ),
    rebuilt: remaining.length > 0,
    count: withExec.length,
  };
}

export function balanceDimKey(row) {
  return [row.budgetKindCode, row.yearNature, row.inoutDiv, lineKey(row)].join('::');
}
